/**
 * chatFiles — station-chat file sharing, client side.
 *
 * station-chat-files slug. Sharing a file in a room is deliberately NOT a new
 * message type: the browser uploads the bytes to the BFF, gets back a
 * root-relative link, and then posts an ORDINARY room message whose text is a
 * markdown link. `MarkdownText` already renders root-relative links and
 * root-relative images, so an image shows inline and anything else shows as a
 * download link — with no change to the message wire, the helper, or the
 * renderer.
 *
 * Upload is driven by PASTE and DRAG-DROP rather than a button, because both
 * hand you the same thing: a `DataTransfer` with a `files` list. A file copied
 * in Finder pastes as a real `File` (with its name and type) in Chrome and
 * Safari; Firefox exposes files on drop but not on paste, which is exactly why
 * both gestures are wired to one handler here.
 */

// Keep in step with AVEQ_CHAT_FILE_MAX_BYTES / _DEFAULT_MAX_BYTES in
// aveq-agent/bff/files.py. This is a courtesy check so an oversized paste fails
// instantly with a clear message instead of after a long upload; the SERVER cap
// is the real one and is enforced while streaming.
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export function humanSize(n) {
  if (!Number.isFinite(n) || n < 0) return '';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = n / 1024;
  for (let i = 0; i < units.length; i += 1) {
    if (v < 1024 || i === units.length - 1) return `${v.toFixed(1)} ${units[i]}`;
    v /= 1024;
  }
  return `${v.toFixed(1)} GB`;
}

/**
 * Which file command, if any, this input line is.
 *
 * These are intercepted in the CLIENT rather than resolved by the LLM: they're
 * UI affordances (open a picker, show a list), not conversation, and the user
 * asked for them to behave like commands. Intercepting also means they never
 * reach the room as chat text — a file listing is personal, not a broadcast.
 *
 * Returns 'upload', 'download', or null.
 */
export function parseFileCommand(text) {
  const t = (text || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!t) return null;
  // Accept the `@aveq …` phrasing and the bare `/…` slash form alike, with the
  // trailing noun optional ("@aveq upload" == "@aveq upload file").
  const m = t.match(/^(?:@aveq\s+|\/)(upload|download|files|attach)(?:\s+(?:a\s+)?files?)?$/);
  if (!m) return null;
  const verb = m[1];
  if (verb === 'upload' || verb === 'attach') return 'upload';
  return 'download';
}

/** Extract real files from a paste or drop event's DataTransfer. */
export function filesFromDataTransfer(dt) {
  if (!dt) return [];
  if (dt.files && dt.files.length) return Array.from(dt.files);
  // Some sources populate `items` but not `files` (notably an image copied from
  // another app rather than from the filesystem).
  if (dt.items && dt.items.length) {
    return Array.from(dt.items)
      .filter((it) => it.kind === 'file')
      .map((it) => it.getAsFile())
      .filter(Boolean);
  }
  return [];
}

/**
 * Upload one file to a station. Resolves to the BFF's descriptor, whose
 * `markdown` field is the exact room-message text to post. That string is built
 * SERVER-side so the link format has a single definition.
 */
export async function uploadFile(stationId, file) {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`${file.name} is ${humanSize(file.size)} — the limit is ${humanSize(MAX_UPLOAD_BYTES)}.`);
  }
  const body = new FormData();
  body.append('file', file, file.name);
  const resp = await fetch(`/api/stations/${stationId}/files`, {
    method: 'POST',
    body,
    credentials: 'same-origin',
  });
  if (!resp.ok) {
    throw new Error(await describeFailure(resp, file));
  }
  return resp.json();
}

// A message that is only an uploaded image: `![name](url)`, optionally followed by the
// `[name](url) (size)` download line the BFF adds (aveq-agent/bff/files.py `_markdown_for`).
const IMAGE_POST_RE =
  /^!\[([^\]\n]*)\]\((\/api\/files\/\d+\/[0-9a-f]{64})\)(?:\n\[[^\]\n]*\]\(\2\)(?: \([^)\n]*\))?)?$/;

/** `{ name, url }` when `text` is an image post and nothing else, else null. */
export function imagePostIn(text) {
  const m = IMAGE_POST_RE.exec(typeof text === 'string' ? text.trim() : '');
  return m ? { name: m[1], url: m[2] } : null;
}

/** On a phone that can share files: the image as a File for the share sheet, else null.
 *  Fetched before the tap, because iOS refuses share() once an await has used the tap up. */
export async function shareableImage(url, name) {
  if (typeof navigator === 'undefined' || !navigator.canShare || !navigator.share) return null;
  if (!window.matchMedia || !window.matchMedia('(pointer: coarse)').matches) return null;
  try {
    const resp = await fetch(url, { credentials: 'same-origin' });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    const file = new File([blob], name || 'image', { type: blob.type });
    return navigator.canShare({ files: [file] }) ? file : null;
  } catch (e) {
    return null;
  }
}

/** Why an image didn't load: 'gone' only when the server says the file doesn't exist;
 *  an outage or a dropped connection is 'failed', so history doesn't read as deleted. */
export async function imageFailure(url) {
  try {
    const resp = await fetch(url, { method: 'HEAD', credentials: 'same-origin' });
    return resp.status === 404 ? 'gone' : 'failed';
  } catch (e) {
    return 'failed';
  }
}

/** List the files a station holds, newest first. */
export async function listFiles(stationId) {
  const resp = await fetch(`/api/stations/${stationId}/files`, {
    credentials: 'same-origin',
  });
  if (!resp.ok) throw new Error(await describeFailure(resp));
  const data = await resp.json();
  return data.files || [];
}

async function describeFailure(resp, file) {
  const who = file ? `${file.name}: ` : '';
  // The BFF puts its reason in the status line; fall back to the body, then to
  // a generic message, so the user always gets something actionable.
  if (resp.status === 401) return `${who}sign in to share files.`;
  if (resp.status === 403) return `${who}you don't have access to this station.`;
  if (resp.status === 413) return `${who}too large (limit ${humanSize(MAX_UPLOAD_BYTES)}).`;
  if (resp.status === 429) return `${who}too many uploads just now — try again in a moment.`;
  if (resp.status === 503) return 'File sharing isn\'t configured on this server.';
  let detail = resp.statusText || '';
  try {
    const text = await resp.text();
    if (text && text.length < 200) detail = text;
  } catch (e) { /* body already consumed or unreadable — keep statusText */ }
  return `${who}upload failed${detail ? ` (${detail})` : ''}.`;
}
